import { ForwardIcon } from '@heroicons/react/24/outline';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface ProgressTestButtonProps {
  onProgress: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  isTest: boolean;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'sm' | 'lg';
  className?: string;

  // Context-aware props
  entityType?: 'letter' | 'cheque' | 'contact' | 'item';
  currentStatus?: string;
  nextStatus?: string;

  // Custom messaging
  buttonText?: string;
  tooltipContent?: string;
  loadingText?: string;
}

const ProgressTestButton = ({
  onProgress,
  disabled = false,
  isLoading = false,
  isTest,
  variant = 'outline',
  size = 'sm',
  className,
  entityType = 'item',
  currentStatus,
  nextStatus,
  buttonText,
  tooltipContent,
  loadingText,
}: ProgressTestButtonProps) => {
  // Only render for test entities
  if (!isTest) {
    return null;
  }

  // Generate context-aware content
  const getButtonText = () => {
    if (buttonText) return buttonText;
    if (isLoading && loadingText) return loadingText;
    if (isLoading) return 'Progressing...';
    if (nextStatus) return `Progress to ${nextStatus}`;
    return 'Progress Test';
  };

  const getTooltipContent = () => {
    if (tooltipContent) return tooltipContent;

    const baseText = `Simulate the next status change for this test ${entityType}`;

    if (currentStatus && nextStatus) {
      return `${baseText}. Will progress from "${currentStatus}" to "${nextStatus}".`;
    }

    if (currentStatus) {
      return `${baseText}. Current status: "${currentStatus}".`;
    }

    return `${baseText}. This only works in test mode and won't affect real deliveries.`;
  };

  const getEntityTypeColor = () => {
    const colors = {
      letter:
        'text-blue-600 border-blue-200 hover:border-blue-300 hover:bg-blue-50',
      cheque:
        'text-green-600 border-green-200 hover:border-green-300 hover:bg-green-50',
      contact:
        'text-purple-600 border-purple-200 hover:border-purple-300 hover:bg-purple-50',
      item: 'text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50',
    };
    return colors[entityType];
  };

  const button = (
    <Button
      variant={variant}
      size={size}
      onClick={onProgress}
      disabled={disabled || isLoading}
      className={cn(
        getEntityTypeColor(),
        'transition-colors duration-200',
        className,
      )}
    >
      {isLoading ? (
        <div className="animate-spin h-4 w-4 mr-2 border-2 border-current border-t-transparent rounded-full" />
      ) : (
        <ForwardIcon className="h-4 w-4 mr-2" />
      )}
      {getButtonText()}
    </Button>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs">
        <div className="space-y-1">
          <p className="font-medium">Progress Test Mode</p>
          <p className="text-sm">{getTooltipContent()}</p>
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

export { ProgressTestButton };
